import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Target, Plus, Trash, Lightbulb, CalendarBlank, FloppyDisk } from "@phosphor-icons/react";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { useCompany } from "../../context/CompanyContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const today = () => new Date().toISOString().slice(0, 10);
const thisMonth = () => new Date().toISOString().slice(0, 7);

export default function MISTargets() {
  const { active, activeId } = useCompany();
  const [emps, setEmps] = useState([]);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [targets, setTargets] = useState([]);
  const [month, setMonth] = useState(thisMonth());
  const [perf, setPerf] = useState({ rows: [], summary: {}, working_days: 0 });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ employee_id: "", client_id: "", service_id: "", target_per_day: 0, unit: "page", effective_from: today(), notes: "" });
  const [dailyOpen, setDailyOpen] = useState(false);
  const [dailyTarget, setDailyTarget] = useState(null);
  const [daily, setDaily] = useState({ rows: [], summary: {} });
  const [dailyLoading, setDailyLoading] = useState(false);

  const load = async () => {
    if (!activeId) return;
    try {
      const [e, c, s, t, p] = await Promise.all([
        api.get("/mis2/employees").catch(() => ({ data: [] })),
        api.get("/mis2/clients", { params: { company_id: activeId } }),
        api.get("/mis2/services", { params: { company_id: activeId } }),
        api.get("/insights/targets", { params: { company_id: activeId } }),
        api.get("/insights/targets/performance", { params: { company_id: activeId, month } }),
      ]);
      setEmps(e.data || []);
      setClients(c.data || []);
      setServices(s.data || []);
      setTargets(t.data || []);
      setPerf(p.data || { rows: [], summary: {} });
    } catch (err) { toast.error(err?.response?.data?.detail || "Load failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeId, month]);

  const save = async () => {
    if (!form.employee_id || !form.target_per_day) { toast.error("Employee + target/day required"); return; }
    try {
      await api.post("/insights/targets", { ...form, company_id: activeId, target_per_day: Number(form.target_per_day) });
      toast.success("Target set");
      setOpen(false);
      setForm({ employee_id: "", client_id: "", service_id: "", target_per_day: 0, unit: "page", effective_from: today(), notes: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete target for ${t.employee_name}?`)) return;
    try { await api.delete(`/insights/targets/${t.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const openDaily = async (t) => {
    setDailyTarget(t);
    setDailyOpen(true);
    setDailyLoading(true);
    try {
      const { data } = await api.get(`/insights/targets/${t.id}/daily`, { params: { month } });
      setDaily(data || { rows: [], summary: {} });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Daily load failed");
      setDaily({ rows: [], summary: {} });
    } finally { setDailyLoading(false); }
  };

  const updateReason = (log_date, reason) => {
    setDaily((d) => ({ ...d, rows: d.rows.map((r) => r.log_date === log_date ? { ...r, reason, _dirty: true } : r) }));
  };

  const saveReason = async (row) => {
    if (!dailyTarget) return;
    try {
      await api.put(`/insights/targets/${dailyTarget.id}/remarks/${row.log_date}`, { target_id: dailyTarget.id, log_date: row.log_date, reason: row.reason || "" });
      setDaily((d) => ({ ...d, rows: d.rows.map((r) => r.log_date === row.log_date ? { ...r, _dirty: false } : r) }));
      toast.success(`Reason saved · ${row.log_date}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const svcsForClient = form.client_id ? services.filter((s) => s.client_id === form.client_id) : services;

  return (
    <Layout
      title="Employee Targets & Revenue Optimizer"
      subtitle={`${active?.name || "—"} · ${month} · ${perf.working_days || 0} working days`}
      actions={
        <>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40 rounded-none" data-testid="tgt-month"/>
          <button onClick={() => setOpen(true)} className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C]" data-testid="tgt-new">
            <Plus size={16}/> Set Target
          </button>
        </>
      }
    >
      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 border border-black/10 bg-white mb-6">
        <Kpi l="Total monthly target" v={perf.summary?.total_target || 0}/>
        <Kpi l="Actual this month" v={perf.summary?.total_actual || 0} c="#0D9488"/>
        <Kpi l="Variation" v={(perf.summary?.total_target || 0) - (perf.summary?.total_actual || 0)} c={((perf.summary?.total_target || 0) - (perf.summary?.total_actual || 0)) > 0 ? "#D90429" : "#0D9488"}/>
        <Kpi l="Gap %" v={perf.summary?.gap_pct || 0} suffix="%" c={(perf.summary?.gap_pct || 0) > 20 ? "#D90429" : "#0D9488"}/>
      </div>

      {perf.rows?.length > 0 ? (
        <div className="border border-black/10 bg-white overflow-x-auto">
          <table className="w-full text-sm min-w-[1000px]" data-testid="tgt-perf-table">
            <thead className="bg-[#F5F6F8] text-left">
              <tr>{["Employee","Client","Service","Target/day","Monthly target","Actual","Variation","Gap %","Recommendation",""].map(h => (
                <th key={h} className="px-3 py-2 font-mono-shelv text-[10px] uppercase tracking-widest text-[#717680]">{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {perf.rows.map((r) => (
                <tr key={r.id} className="border-t border-black/5" data-testid={`tgt-row-${r.id}`}>
                  <td className="px-3 py-2.5 font-semibold">{r.employee_name}</td>
                  <td className="px-3 py-2.5 text-[12px]">{r.client_name || "All clients"}</td>
                  <td className="px-3 py-2.5 text-[12px]">{r.service_name || "All services"}</td>
                  <td className="px-3 py-2.5 font-mono-shelv">{r.target_per_day}/{r.unit}</td>
                  <td className="px-3 py-2.5 font-mono-shelv">{Number(r.monthly_target).toLocaleString("en-IN")}</td>
                  <td className="px-3 py-2.5 font-mono-shelv font-bold text-[#0D9488]">{Number(r.actual).toLocaleString("en-IN")}</td>
                  <td className="px-3 py-2.5 font-mono-shelv font-bold" style={{ color: r.variation > 0 ? "#D90429" : "#0D9488" }} data-testid={`tgt-variation-${r.id}`}>{Number(r.variation).toLocaleString("en-IN")}</td>
                  <td className="px-3 py-2.5 font-mono-shelv" style={{ color: r.gap_pct > 20 ? "#D90429" : "#0D9488" }}>{r.gap_pct}%</td>
                  <td className="px-3 py-2.5 text-[12px] max-w-sm">
                    <Lightbulb size={12} weight="bold" className="inline mr-1 text-[#F4A261]"/>
                    {r.recommendation}
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    <button onClick={() => openDaily(r)} className="p-1.5 hover:bg-black/5 text-[#0A0A0A] mr-1" title="Daily breakdown" data-testid={`tgt-daily-${r.id}`}>
                      <CalendarBlank size={14}/>
                    </button>
                    <button onClick={() => remove(r)} className="p-1.5 hover:bg-[#D90429]/10 text-[#D90429]" title="Delete"><Trash size={12}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="border border-black/10 bg-white p-16 text-center text-[#717680]" data-testid="tgt-empty-state">
          <Target size={36} className="mx-auto text-[#D90429] mb-3"/>
          <p>No targets set yet. Click <b>Set Target</b> to define daily quotas per employee, client &amp; service (e.g. Raja · E-stamping · 6000/day).</p>
        </div>
      )}

      {/* Create-target dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-lg">
          <DialogHeader><DialogTitle className="font-display tracking-tight">Set employee target</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Employee *</Label>
              <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                <SelectTrigger className="rounded-none" data-testid="tgt-emp-select"><SelectValue placeholder="Pick employee"/></SelectTrigger>
                <SelectContent>{emps.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Client (optional — leave blank = all clients)</Label>
              <Select value={form.client_id || "__all__"} onValueChange={(v) => setForm({ ...form, client_id: v === "__all__" ? "" : v, service_id: "" })}>
                <SelectTrigger className="rounded-none"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">— all clients —</SelectItem>
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Service (optional — leave blank = all services)</Label>
              <Select value={form.service_id || "__all__"} onValueChange={(v) => setForm({ ...form, service_id: v === "__all__" ? "" : v })}>
                <SelectTrigger className="rounded-none"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">— all services —</SelectItem>
                  {svcsForClient.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Target per day *</Label><Input type="number" value={form.target_per_day} onChange={(e) => setForm({ ...form, target_per_day: e.target.value })} className="rounded-none" data-testid="tgt-per-day"/></div>
            <div><Label>Unit</Label><Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="rounded-none"/></div>
            <div><Label>Effective from</Label><Input type="date" value={form.effective_from} onChange={(e) => setForm({ ...form, effective_from: e.target.value })} className="rounded-none"/></div>
            <div className="col-span-2"><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-none"/></div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 py-2 border border-black/15 text-sm">Cancel</button>
            <button onClick={save} className="px-4 py-2 bg-[#D90429] text-white text-sm font-semibold" data-testid="tgt-save">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Daily breakdown dialog */}
      <Dialog open={dailyOpen} onOpenChange={setDailyOpen}>
        <DialogContent className="rounded-none max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              Daily production vs target · {dailyTarget?.employee_name}
              <span className="text-sm text-[#717680] font-normal ml-2">
                ({dailyTarget?.client_name || "All clients"} · {dailyTarget?.service_name || "All services"} · {dailyTarget?.target_per_day}/{dailyTarget?.unit} per day · {month})
              </span>
            </DialogTitle>
          </DialogHeader>
          {dailyLoading ? (
            <p className="text-sm text-[#717680] p-4">Loading…</p>
          ) : (
            <>
              <div className="grid grid-cols-3 border border-black/10 bg-[#F5F6F8] mb-4">
                <MiniKpi l="Monthly target" v={daily.summary?.monthly_target || 0}/>
                <MiniKpi l="Actual" v={daily.summary?.monthly_actual || 0} c="#0D9488"/>
                <MiniKpi l="Variation" v={daily.summary?.monthly_variation || 0} c={(daily.summary?.monthly_variation || 0) > 0 ? "#D90429" : "#0D9488"}/>
              </div>
              <div className="overflow-x-auto border border-black/10">
                <table className="w-full text-sm" data-testid="tgt-daily-table">
                  <thead className="bg-[#F5F6F8] text-left sticky top-0">
                    <tr>{["Date","Day","Target","Actual","Variation","Reason for shortfall",""].map(h => (
                      <th key={h} className="px-3 py-2 font-mono-shelv text-[10px] uppercase tracking-widest text-[#717680]">{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody>
                    {daily.rows.map((r) => (
                      <tr key={r.log_date} className={`border-t border-black/5 ${r.is_off ? "bg-black/[0.02] text-[#717680]" : ""}`} data-testid={`tgt-daily-row-${r.log_date}`}>
                        <td className="px-3 py-2 font-mono-shelv text-[12px]">{r.log_date.slice(-2)}</td>
                        <td className="px-3 py-2 text-[12px]">{r.weekday}{r.is_off && " · off"}</td>
                        <td className="px-3 py-2 font-mono-shelv">{Number(r.target).toLocaleString("en-IN")}</td>
                        <td className="px-3 py-2 font-mono-shelv text-[#0D9488]">{Number(r.actual).toLocaleString("en-IN")}</td>
                        <td className="px-3 py-2 font-mono-shelv font-bold" style={{ color: r.variation > 0 ? "#D90429" : "#0D9488" }}>{Number(r.variation).toLocaleString("en-IN")}</td>
                        <td className="px-3 py-2">
                          <Input
                            value={r.reason || ""}
                            onChange={(e) => updateReason(r.log_date, e.target.value)}
                            placeholder={r.is_off ? "" : (r.variation > 0 ? "Why fell short?" : "—")}
                            disabled={r.is_off}
                            className="rounded-none h-8 text-[12px]"
                            data-testid={`tgt-reason-${r.log_date}`}
                          />
                        </td>
                        <td className="px-3 py-2">
                          {r._dirty && (
                            <button onClick={() => saveReason(r)} className="p-1.5 bg-[#D90429] text-white hover:bg-[#EF233C]" title="Save reason" data-testid={`tgt-save-reason-${r.log_date}`}>
                              <FloppyDisk size={12} weight="bold"/>
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          <DialogFooter>
            <button onClick={() => setDailyOpen(false)} className="px-4 py-2 border border-black/15 text-sm" data-testid="tgt-daily-close">Close</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function Kpi({ l, v, c = "#0A0A0A", suffix = "" }) {
  return (
    <div className="p-5 border-r border-black/5 last:border-r-0">
      <div className="font-mono-shelv text-[10px] tracking-widest uppercase text-[#D90429]">{l}</div>
      <div className="font-display text-3xl font-black tracking-tighter mt-2" style={{ color: c }}>{Number(v).toLocaleString("en-IN")}{suffix}</div>
    </div>
  );
}

function MiniKpi({ l, v, c = "#0A0A0A" }) {
  return (
    <div className="p-3 border-r border-black/5 last:border-r-0">
      <div className="font-mono-shelv text-[9px] tracking-widest uppercase text-[#717680]">{l}</div>
      <div className="font-display text-xl font-black tracking-tighter" style={{ color: c }}>{Number(v).toLocaleString("en-IN")}</div>
    </div>
  );
}
